'use client';

// Third Party
import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import dayjs from '@utils/dayjs-setup';

// Utils
import { useMobileSearchModal } from './mobile-search-modal-context';
import { guestDescription } from '@utils/guest-description';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';
import { Geometry } from '@contexts/search-context';
import { Feature } from '@contexts/search-context';
import { routes } from '@routes';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';

// UI
import { BaseModal } from '@components/base-modal';
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { MobileSelectAutocomplete } from './mobile-select-autocomplete';
import { Typography } from '@components/typography';
import { SearchDateRangePicker, type FocusedInputShape, type SearchDateRange } from '@components/search-date-range-picker';
type Props = {
  placeName?: string;
  placeId?: string;
  checkInDate?: string | null;
  checkOutDate?: string | null;
  adultCount?: number;
  childCount?: number;
  infantCount?: number;
  petCount?: number;
  activeFilters?: Feature[];
  mapZoom?: number;
  geoSearchBox?: Geometry;
  onSubmit?: (callbackArgs: {
    placeId: string;
    checkInDate: string | null;
    checkOutDate: string | null;
    adultCount: number;
    childCount: number;
    infantCount: number;
    petCount: number;
    activeFilters?: Feature[];
  }) => void;
};
type Place = {
  id: string;
  name: string;
};
const placeFromProps = (name?: string, id?: string): Place | undefined => name ? {
  name,
  id: id || ''
} : undefined;
const isSamePlace = (a?: Place, b?: Place) => a?.name === b?.name && a?.id === b?.id;
function usePlaceFromPropsWhileClosed(fromProps, isModalOpen) {
  const $ = _c(2);
  const [place, setPlace] = React.useState(fromProps);
  const [seenFromProps, setSeenFromProps] = React.useState(fromProps);
  if (!isModalOpen && !isSamePlace(seenFromProps, fromProps)) {
    setSeenFromProps(fromProps);
    setPlace(fromProps);
  }
  let t0;
  if ($[0] !== place) {
    t0 = [place, setPlace];
    $[0] = place;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0 as const;
}
export const MobileSearchModal: React.FC<Props> = t0 => {
  const $ = _c(118);
  const {
    placeName: _placeName,
    placeId: _placeId,
    checkInDate: t1,
    checkOutDate: t2,
    adultCount: t3,
    childCount: t4,
    infantCount: t5,
    petCount: t6,
    activeFilters,
    mapZoom,
    onSubmit: _onSubmit
  } = t0;
  const _checkInDate = t1 === undefined ? null : t1;
  const _checkOutDate = t2 === undefined ? null : t2;
  const _adultCount = t3 === undefined ? 1 : t3;
  const _childCount = t4 === undefined ? 0 : t4;
  const _infantCount = t5 === undefined ? 0 : t5;
  const _petCount = t6 === undefined ? 0 : t6;
  const {
    mobileSearchModalActive,
    deactivateMobileSearchModal
  } = useMobileSearchModal();
  const [checkInDate, setCheckInDate] = React.useState(_checkInDate);
  const [checkOutDate, setCheckOutDate] = React.useState(_checkOutDate);
  const [adultCount, setAdultCount] = React.useState(_adultCount);
  const [childCount, setChildCount] = React.useState(_childCount);
  const [infantCount, setInfantCount] = React.useState(_infantCount);
  const [petCount, setPetCount] = React.useState(_petCount);
  let t7;
  if ($[0] !== _placeId || $[1] !== _placeName) {
    t7 = placeFromProps(_placeName, _placeId);
    $[0] = _placeId;
    $[1] = _placeName;
    $[2] = t7;
  } else {
    t7 = $[2];
  }
  const [place, setPlace] = usePlaceFromPropsWhileClosed(t7, mobileSearchModalActive);
  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] = React.useState("startDate");
  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] = React.useState("");
  let t8;
  let t9;
  if ($[3] !== adultCount || $[4] !== childCount || $[5] !== infantCount || $[6] !== petCount) {
    t8 = () => {
      const description = guestDescription(adultCount, childCount, infantCount, petCount);
      setGuestCountHumanReadableValue(description);
    };
    t9 = [adultCount, childCount, infantCount, petCount];
    $[3] = adultCount;
    $[4] = childCount;
    $[5] = infantCount;
    $[6] = petCount;
    $[7] = t8;
    $[8] = t9;
  } else {
    t8 = $[7];
    t9 = $[8];
  }
  React.useEffect(t8, t9);
  let t10;
  let t11;
  if ($[9] !== _adultCount || $[10] !== _childCount || $[11] !== _infantCount || $[12] !== _petCount) {
    t10 = () => {
      setAdultCount(_adultCount);
      setChildCount(_childCount);
      setInfantCount(_infantCount);
      setPetCount(_petCount);
    };
    t11 = [_adultCount, _childCount, _infantCount, _petCount];
    $[9] = _adultCount;
    $[10] = _childCount;
    $[11] = _infantCount;
    $[12] = _petCount;
    $[13] = t10;
    $[14] = t11;
  } else {
    t10 = $[13];
    t11 = $[14];
  }
  React.useEffect(t10, t11);
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = range => {
      setCheckInDate && setCheckInDate(range.checkInDate);
      setCheckOutDate && setCheckOutDate(range.checkOutDate);
    };
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  const handleDatesChange = t12;
  let t13;
  if ($[16] !== setPlace) {
    t13 = () => {
      setPlace(undefined);
      setAdultCount(1);
      setChildCount(0);
      setInfantCount(0);
      setPetCount(0);
      setDateRangePickerFocusedInput("startDate");
      setCheckInDate && setCheckInDate(null);
      setCheckOutDate && setCheckOutDate(null);
    };
    $[16] = setPlace;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const clearSelectedFilters = t13;
  const [searchInputOpen, setSearchInputOpen] = React.useState(true);
  const [dateInputOpen, setDateInputOpen] = React.useState(false);
  const [guestInputOpen, setGuestInputOpen] = React.useState(false);
  const handleValueChange = _temp;
  let t14;
  if ($[18] !== _onSubmit || $[19] !== activeFilters || $[20] !== adultCount || $[21] !== checkInDate || $[22] !== checkOutDate || $[23] !== childCount || $[24] !== infantCount || $[25] !== mapZoom || $[26] !== petCount || $[27] !== place?.id) {
    t14 = event => {
      event.preventDefault();
      const queryParams = new URLSearchParams(window.location.search);
      _onSubmit && _onSubmit({
        placeId: place?.id || "ChIJCzYy5IS16lQRQrfeQ5K5Oxw",
        checkInDate,
        checkOutDate,
        adultCount,
        childCount,
        infantCount,
        petCount,
        activeFilters
      });
      !_onSubmit && window.location.assign(routes.searchShow({
        placeId: place?.id || undefined,
        zoom: place?.id ? undefined : queryParams.get("zoom") || mapZoom?.toString() || undefined,
        centerLat: place?.id ? undefined : queryParams.get("centerLat") || undefined,
        centerLng: place?.id ? undefined : queryParams.get("centerLng") || undefined,
        southWestLat: place?.id ? undefined : queryParams.get("southWestLat") || undefined,
        southWestLng: place?.id ? undefined : queryParams.get("southWestLng") || undefined,
        northEastLat: place?.id ? undefined : queryParams.get("northEastLat") || undefined,
        northEastLng: place?.id ? undefined : queryParams.get("northEastLng") || undefined,
        checkInDate: checkInDate || undefined,
        checkOutDate: checkOutDate || undefined,
        adultCount,
        childCount,
        infantCount,
        petCount,
        activeFeatures: activeFilters && activeFilters.map(_temp2).join(",")
      }));
    };
    $[18] = _onSubmit;
    $[19] = activeFilters;
    $[20] = adultCount;
    $[21] = checkInDate;
    $[22] = checkOutDate;
    $[23] = childCount;
    $[24] = infantCount;
    $[25] = mapZoom;
    $[26] = petCount;
    $[27] = place?.id;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const onSubmit = t14;
  let t15;
  if ($[29] !== _placeId || $[30] !== _placeName || $[31] !== deactivateMobileSearchModal || $[32] !== setPlace) {
    t15 = <div className="flex flex-row justify-start px-2 py-1 border-b border-gray-light"><ButtonIcon variant="text" color="gray" size="md" isCircle={true} icon={IconNames.X} ariaDescribedby="close-mobile-search-form" ariaLabel="Close" className="h-10 w-10 justify-center items-center z-100d" onClick={() => {
        deactivateMobileSearchModal();
        setSearchInputOpen(true);
        setDateInputOpen(false);
        setGuestInputOpen(false);
        setPlace(placeFromProps(_placeName, _placeId));
      }} /></div>;
    $[29] = _placeId;
    $[30] = _placeName;
    $[31] = deactivateMobileSearchModal;
    $[32] = setPlace;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== searchInputOpen) {
    t16 = () => setSearchInputOpen(!searchInputOpen);
    $[34] = searchInputOpen;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex flex-col w-1/3 text-black text-left"><Typography variant="subtitle1" component="p">Where</Typography></div>;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== place?.name) {
    t18 = place?.name ? `${extractFirstPhraseBeforeComma(place?.name)}` : "Anywhere";
    $[37] = place?.name;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t18) {
    t19 = <div className="flex flex-col w-2/3 text-right truncate">{t18}</div>;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t16 || $[42] !== t19) {
    t20 = <Button variant="text" onClick={t16} className="flex flex-cols-2 items-center space-x-2 w-full">{t17}{t19}</Button>;
    $[41] = t16;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== place || $[45] !== searchInputOpen || $[46] !== setPlace) {
    t21 = searchInputOpen && <div className="pt-2 overflow-auto"><MobileSelectAutocomplete placeName={place?.name ? place.name : ""} onChange={(_name, option) => {
        if (option) {
          setPlace({
            name: option.label,
            id: option.value
          });
          setSearchInputOpen(false);
          setDateInputOpen(true);
        }
      }} onClear={() => setPlace(undefined)} initialValue={place && place.name && place.id ? {
        label: place.name,
        value: place.id
      } : null} /></div>;
    $[44] = place;
    $[45] = searchInputOpen;
    $[46] = setPlace;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t20 || $[49] !== t21) {
    t22 = <div className="px-4 p-4 bg-white rounded-2xl shadow-md">{t20}{t21}</div>;
    $[48] = t20;
    $[49] = t21;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== dateInputOpen) {
    t23 = () => setDateInputOpen(!dateInputOpen);
    $[51] = dateInputOpen;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="flex flex-col text-black text-left"><Typography variant="subtitle1" component="p">When</Typography></div>;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== checkInDate || $[55] !== checkOutDate) {
    t25 = checkInDate && checkOutDate ? `${dayjs(checkInDate).format("M/DD")} - ${dayjs(checkOutDate).format("M/DD")}` : "Add Dates";
    $[54] = checkInDate;
    $[55] = checkOutDate;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t25) {
    t26 = <div className="flex flex-col w-full text-right items-end">{t25}</div>;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t23 || $[60] !== t26) {
    t27 = <Button variant="text" onClick={t23} className="flex flex-cols-2 items-center space-x-2 w-full justify-between">{t24}{t26}</Button>;
    $[59] = t23;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== checkInDate || $[63] !== checkOutDate || $[64] !== dateInputOpen || $[65] !== dateRangePickerFocusedInput) {
    t28 = dateInputOpen && <div className=""><div className="hidden"><Input label="Check-in" name="checkInDate" value={checkInDate ? dayjs(checkInDate).format("MMM DD") : ""} onChange={_temp3} dataId="@filter/check-in-date" /><Input label="Check-out" name="checkOutDate" value={checkOutDate ? dayjs(checkOutDate).format("MMM DD") : ""} onChange={_temp4} dataId="@filter/check-out-date" /></div><div className="w-full items-center bg-white"><div className="flex h-full w-full z-50 justify-center bg-white"><SearchDateRangePicker numberOfMonths={1} focusedInput={dateRangePickerFocusedInput} onFocusChange={focusedInput => {
            setDateRangePickerFocusedInput(focusedInput || "startDate");
          }} checkInDate={checkInDate} checkOutDate={checkOutDate} onDatesChange={handleDatesChange} /></div><div className="flex w-full justify-between pr-2">{!checkInDate && !checkOutDate && <Button variant="text" onClick={() => {
            setDateInputOpen(false);
            setGuestInputOpen(true);
          }}>Skip</Button>}{checkInDate && <Button variant="text" onClick={() => {
            setCheckInDate(null);
            setCheckOutDate(null);
            setDateRangePickerFocusedInput("startDate");
          }} dataId="@filter/clear-dates">Clear</Button>}<Button onClick={() => {
            setDateInputOpen(false);
            setGuestInputOpen(true);
          }} dataId="@filter/next" variant="outlined">Next</Button></div></div></div>;
    $[62] = checkInDate;
    $[63] = checkOutDate;
    $[64] = dateInputOpen;
    $[65] = dateRangePickerFocusedInput;
    $[66] = t28;
  } else {
    t28 = $[66];
  }
  let t29;
  if ($[67] !== t27 || $[68] !== t28) {
    t29 = <div className="px-4 p-4 bg-white rounded-2xl shadow-md">{t27}{t28}</div>;
    $[67] = t27;
    $[68] = t28;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== guestInputOpen) {
    t30 = () => setGuestInputOpen(!guestInputOpen);
    $[70] = guestInputOpen;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="flex flex-col text-black text-left"><Typography variant="subtitle1" component="p">Who</Typography></div>;
    $[72] = t31;
  } else {
    t31 = $[72];
  }
  const t32 = guestCountHumanReadableValue ? `${guestCountHumanReadableValue}` : "Add Guests";
  let t33;
  if ($[73] !== t32) {
    t33 = <div className="flex flex-col w-2/3 text-right items-end"><div className="w-full truncate">{t32}</div></div>;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t30 || $[76] !== t33) {
    t34 = <Button variant="text" onClick={t30} className="flex flex-cols-2 items-center space-x-2 w-full justify-between">{t31}{t33}</Button>;
    $[75] = t30;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== adultCount || $[79] !== childCount || $[80] !== guestInputOpen || $[81] !== infantCount || $[82] !== petCount) {
    t35 = guestInputOpen && <div className="flex h-full w-full justify-center mx-auto bg-white"><div className="w-full p-4 space-y-4 divide-y divide-gray-light"><div className="flex justify-between"><div className="flex flex-col text-left"><Typography variant="subtitle2" component="p" className="pb-2">Adults</Typography><Typography variant="body2" component="p">Ages 13 or above</Typography></div><div className="flex space-x-2 justify-between"><ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-adult" ariaLabel="Remove adult" variant="text" className="text-gray-main" onClick={() => setAdultCount(_temp5)} dataId="@filter/remove-adult-count" /><input type="number" aria-label="Number of adults" className="w-6 text-center" value={adultCount} onChange={e_0 => {
              handleValueChange(e_0, setAdultCount, guestCapacity.ADULTS);
            }} /><ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-adult" ariaLabel="Add adult" variant="text" className="text-gray-main" onClick={() => setAdultCount(_temp6)} dataId="@filter/add-adult-count" /></div></div><div className="flex justify-between pt-4"><div className="flex flex-col"><Typography variant="subtitle2" component="p" className="pb-2">Children</Typography><Typography variant="body2" component="p">Ages 2-12</Typography></div><div className="flex space-x-2 justify-between"><ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-child-count" ariaLabel="Remove child" variant="text" className="text-gray-main" onClick={() => setChildCount(_temp7)} dataId="@filter/remove-child-count" /><input type="number" aria-label="Number of children" className="w-6 text-center" value={childCount} onChange={e_1 => {
              handleValueChange(e_1, setChildCount, guestCapacity.CHILDREN);
            }} /><ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-child-count" ariaLabel="Add child" variant="text" className="text-gray-main" onClick={() => setChildCount(_temp8)} dataId="@filter/add-child-count" /></div></div><div className="flex justify-between pt-4"><div className="flex flex-col"><Typography variant="subtitle2" component="p" className="pb-2">Infants</Typography><Typography variant="body2" component="p">Under 2</Typography></div><div className="flex space-x-2 justify-between"><ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-infant-count" ariaLabel="Remove infant" variant="text" className="text-gray-main" onClick={() => setInfantCount(_temp9)} dataId="@filter/remove-infant-count" /><input type="number" aria-label="Number of infants" className="w-6 text-center" value={infantCount} onChange={e_2 => {
              handleValueChange(e_2, setInfantCount, guestCapacity.INFANTS);
            }} /><ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-infant-count" ariaLabel="Add infant" variant="text" className="text-gray-main" onClick={() => setInfantCount(_temp10)} dataId="@filter/add-infant-count" /></div></div><div className="flex justify-between pt-4"><div className="flex flex-col"><Typography variant="subtitle2" component="p" className="pb-2">Pets</Typography><Typography variant="body2" component="p">Only properties that allow pets will be shown</Typography></div><div className="flex space-x-2 justify-between"><ButtonIcon icon={IconNames.MINUS} ariaDescribedby="remove-pet-count" ariaLabel="Remove pet" variant="text" className="text-gray-main" onClick={() => setPetCount(_temp11)} dataId="@filter/remove-pet-count" /><input type="number" aria-label="Number of pets" className="w-6 text-center" value={petCount} onChange={e_3 => {
              handleValueChange(e_3, setPetCount, guestCapacity.PETS);
            }} /><ButtonIcon icon={IconNames.PLUS} ariaDescribedby="add-pet-count" ariaLabel="Add pet" variant="text" className="text-gray-main" onClick={() => setPetCount(_temp12)} dataId="@filter/add-pet-count" /></div></div></div></div>;
    $[78] = adultCount;
    $[79] = childCount;
    $[80] = guestInputOpen;
    $[81] = infantCount;
    $[82] = petCount;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t34 || $[85] !== t35) {
    t36 = <div className="px-4 p-4 bg-white rounded-2xl shadow-md">{t34}{t35}</div>;
    $[84] = t34;
    $[85] = t35;
    $[86] = t36;
  } else {
    t36 = $[86];
  }
  let t37;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <div className="h-36 block w-full" />;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t38 = <Typography variant="subtitle2" className="text-black">Clear all filters</Typography>;
    $[88] = t38;
  } else {
    t38 = $[88];
  }
  let t39;
  if ($[89] !== clearSelectedFilters) {
    t39 = <div className="w-full text-left text-black"><Button onClick={clearSelectedFilters} variant="text" dataId="@filter/clear-all-filters">{t38}</Button></div>;
    $[89] = clearSelectedFilters;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  if ($[91] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = () => {
      setSearchInputOpen(false);
      setDateInputOpen(false);
      setGuestInputOpen(false);
    };
    $[91] = t40;
  } else {
    t40 = $[91];
  }
  let t41;
  if ($[92] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <div className="text-right  w-full items-end"><Button type="submit" className="ml-2" onClick={t40} dataId="@filter/search-properties"><Icon name={IconNames.MAGNIFYING_GLASS} solid={true} className="pr-2" />Search</Button></div>;
    $[92] = t41;
  } else {
    t41 = $[92];
  }
  let t42;
  if ($[93] !== t39) {
    t42 = <div className="-ml-4 block absolute w-full -bottom-0 bg-white pb-3 border-t -mx-4 sm:px-8 md:hidden border border-gray-light shadow-2xl px-4 pt-4 z-40"><div className="flex justify-between items-center"><div className="-ml-1 flex overflow-x-auto w-full justify-center items-center text-center"><>{t39}{t41}</></div></div></div>;
    $[93] = t39;
    $[94] = t42;
  } else {
    t42 = $[94];
  }
  let t43;
  if ($[95] !== onSubmit || $[96] !== t22 || $[97] !== t29 || $[98] !== t36 || $[99] !== t42) {
    t43 = <div className="flex-1 overflow-y-auto pt-4"><div className="flex flex-col h-full"><form id="mobile-search-form" onSubmit={onSubmit} className="space-y-2 mx-4 pb-24">{t22}{t29}{t36}{t37}{t42}</form></div></div>;
    $[95] = onSubmit;
    $[96] = t22;
    $[97] = t29;
    $[98] = t36;
    $[99] = t42;
    $[100] = t43;
  } else {
    t43 = $[100];
  }
  let t44;
  if ($[101] !== clearSelectedFilters) {
    t44 = () => clearSelectedFilters();
    $[101] = clearSelectedFilters;
    $[102] = t44;
  } else {
    t44 = $[102];
  }
  let t45;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <Typography variant="subtitle2" className="text-black">Clear all filters</Typography>;
    $[103] = t45;
  } else {
    t45 = $[103];
  }
  let t46;
  if ($[104] !== t44) {
    t46 = <div className="w-full text-left text-black"><Button onClick={t44} variant="text" dataId="@filter/clear-all-filters">{t45}</Button></div>;
    $[104] = t44;
    $[105] = t46;
  } else {
    t46 = $[105];
  }
  let t47;
  if ($[106] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = () => {
      setSearchInputOpen(false);
      setDateInputOpen(false);
      setGuestInputOpen(false);
    };
    $[106] = t47;
  } else {
    t47 = $[106];
  }
  let t48;
  if ($[107] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <div className="text-right  w-full items-end"><Button type="submit" form="mobile-search-form" className="ml-2" onClick={t47} dataId="@filter/search-properties"><Icon name={IconNames.MAGNIFYING_GLASS} solid={true} className="text-white pr-2" />Search</Button></div>;
    $[107] = t48;
  } else {
    t48 = $[107];
  }
  let t49;
  if ($[108] !== t46) {
    t49 = <div className="sticky bottom-0 bg-white pb-3 border-t border-gray-light shadow-2xl px-4 pt-4"><div className="flex justify-between items-center"><div className="-ml-1 flex overflow-x-auto w-full justify-center items-center text-center"><>{t46}{t48}</></div></div></div>;
    $[108] = t46;
    $[109] = t49;
  } else {
    t49 = $[109];
  }
  let t50;
  if ($[110] !== t15 || $[111] !== t43 || $[112] !== t49) {
    t50 = <div className="absolute inset-0 flex flex-col h-full">{t15}{t43}{t49}</div>;
    $[110] = t15;
    $[111] = t43;
    $[112] = t49;
    $[113] = t50;
  } else {
    t50 = $[113];
  }
  let t51;
  if ($[114] !== deactivateMobileSearchModal || $[115] !== mobileSearchModalActive || $[116] !== t50) {
    t51 = <BaseModal isOpen={mobileSearchModalActive} onClose={deactivateMobileSearchModal} size="full" zIndexLevel="fullScreenModal" showCloseButton={false} className="bg-gray-xlight">{t50}</BaseModal>;
    $[114] = deactivateMobileSearchModal;
    $[115] = mobileSearchModalActive;
    $[116] = t50;
    $[117] = t51;
  } else {
    t51 = $[117];
  }
  return t51;
};
function _temp(e, setValue, maxValue) {
  e.preventDefault();
  const re = /^[0-9\b]+$/;
  if (e.target.value.startsWith("0")) {
    e.target.value = e.target.value.slice(1);
  }
  if (e.target.value === "" || re.test(e.target.value)) {
    const inputValue = Number(e.target.value);
    const value = inputValue > maxValue ? maxValue : inputValue;
    setValue(value);
  }
}
function _temp2(f) {
  return f.key;
}
function _temp3() {}
function _temp4() {}
function _temp5(val) {
  if (val > 1) {
    return val - 1;
  } else {
    return val;
  }
}
function _temp6(val_0) {
  return val_0 >= guestCapacity.ADULTS ? guestCapacity.ADULTS : val_0 + 1;
}
function _temp7(val_1) {
  if (val_1 > 0) {
    return val_1 - 1;
  } else {
    return val_1;
  }
}
function _temp8(val_2) {
  return val_2 >= guestCapacity.CHILDREN ? guestCapacity.CHILDREN : val_2 + 1;
}
function _temp9(val_3) {
  if (val_3 > 0) {
    return val_3 - 1;
  } else {
    return val_3;
  }
}
function _temp10(val_4) {
  return val_4 >= guestCapacity.INFANTS ? guestCapacity.INFANTS : val_4 + 1;
}
function _temp11(val_5) {
  if (val_5 > 0) {
    return val_5 - 1;
  } else {
    return val_5;
  }
}
function _temp12(val_6) {
  return val_6 >= guestCapacity.PETS ? guestCapacity.PETS : val_6 + 1;
}